/**
 * Theme utility functions for EDX Red
 * Handles theme preferences and customization
 */

// Available themes
export const THEMES = {
    DARK: 'dark',
    LIGHT: 'light',
    BLUE: 'blue',
    GREEN: 'green',
    PURPLE: 'purple',
  };
  
  /**
   * Get the user's theme preference
   * Checks localStorage first, then falls back to system preference or default
   * @returns {string} Theme name
   */
  export const getThemePreference = () => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && Object.values(THEMES).includes(savedTheme)) {
      return savedTheme;
    }
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return THEMES.DARK;
    }
    
    // Default to light theme
    return THEMES.LIGHT;
  };
  
  /**
   * Apply theme CSS variables to the document root
   * @param {string} theme - Theme name
   */
  export const applyTheme = (theme) => {
    // Set the theme attribute on the document
    document.documentElement.setAttribute('data-theme', theme);
    
    // Add or remove dark class for Tailwind
    if (theme === THEMES.DARK || theme === THEMES.CHAOS) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Apply CSS variables based on the theme
    const root = document.documentElement;
    
    switch (theme) {
      case THEMES.DARK:
        root.style.setProperty('--bg-primary', '#121212');
        root.style.setProperty('--bg-secondary', '#1e1e1e');
        root.style.setProperty('--text-primary', '#ffffff');
        root.style.setProperty('--text-secondary', '#b3b3b3');
        root.style.setProperty('--accent-color', '#bb86fc');
        root.style.setProperty('--error-color', '#cf6679');
        break;
        
      case THEMES.LIGHT:
        root.style.setProperty('--bg-primary', '#ffffff');
        root.style.setProperty('--bg-secondary', '#f5f5f5');
        root.style.setProperty('--text-primary', '#121212');
        root.style.setProperty('--text-secondary', '#6e6e6e');
        root.style.setProperty('--accent-color', '#6200ee');
        root.style.setProperty('--error-color', '#b00020');
        break;
        
      case THEMES.BLUE:
        root.style.setProperty('--bg-primary', '#e3f2fd');
        root.style.setProperty('--bg-secondary', '#bbdefb');
        root.style.setProperty('--text-primary', '#0d47a1');
        root.style.setProperty('--text-secondary', '#1976d2');
        root.style.setProperty('--accent-color', '#2962ff');
        root.style.setProperty('--error-color', '#d50000');
        break;
        
      case THEMES.GREEN:
        root.style.setProperty('--bg-primary', '#e8f5e9');
        root.style.setProperty('--bg-secondary', '#c8e6c9');
        root.style.setProperty('--text-primary', '#1b5e20');
        root.style.setProperty('--text-secondary', '#388e3c');
        root.style.setProperty('--accent-color', '#00c853');
        root.style.setProperty('--error-color', '#d50000');
        break;
        
      case THEMES.PURPLE:
        root.style.setProperty('--bg-primary', '#f3e5f5');
        root.style.setProperty('--bg-secondary', '#e1bee7');
        root.style.setProperty('--text-primary', '#4a148c');
        root.style.setProperty('--text-secondary', '#7b1fa2');
        root.style.setProperty('--accent-color', '#aa00ff');
        root.style.setProperty('--error-color', '#d50000');
        break;
        
      default:
        // Fallback to dark theme
        root.style.setProperty('--bg-primary', '#121212');
        root.style.setProperty('--bg-secondary', '#1e1e1e');
        root.style.setProperty('--text-primary', '#ffffff');
        root.style.setProperty('--text-secondary', '#b3b3b3');
        root.style.setProperty('--accent-color', '#bb86fc');
        root.style.setProperty('--error-color', '#cf6679');
        break;
    }
    
    // Save the theme preference
    localStorage.setItem('theme', theme);
  };
  
  /**
   * Get CSS variables for a specific theme
   * Useful for previewing themes
   * @param {string} theme - Theme name
   * @returns {Object} Theme colors
   */
  export const getThemeColors = (theme) => {
    switch (theme) {
      case THEMES.DARK:
        return {
          bgPrimary: '#121212',
          bgSecondary: '#1e1e1e',
          textPrimary: '#ffffff',
          textSecondary: '#b3b3b3',
          accent: '#bb86fc',
          error: '#cf6679',
        };
        
      case THEMES.LIGHT:
        return {
          bgPrimary: '#ffffff',
          bgSecondary: '#f5f5f5',
          textPrimary: '#121212',
          textSecondary: '#6e6e6e',
          accent: '#6200ee',
          error: '#b00020',
        };
        
      case THEMES.BLUE:
        return {
          bgPrimary: '#e3f2fd',
          bgSecondary: '#bbdefb',
          textPrimary: '#0d47a1',
          textSecondary: '#1976d2',
          accent: '#2962ff',
          error: '#d50000',
        };
        
      case THEMES.GREEN:
        return {
          bgPrimary: '#e8f5e9',
          bgSecondary: '#c8e6c9',
          textPrimary: '#1b5e20',
          textSecondary: '#388e3c',
          accent: '#00c853',
          error: '#d50000',
        };
        
      case THEMES.PURPLE:
        return {
          bgPrimary: '#f3e5f5',
          bgSecondary: '#e1bee7',
          textPrimary: '#4a148c',
          textSecondary: '#7b1fa2',
          accent: '#aa00ff',
          error: '#d50000',
        };
        
      default:
        // Fallback to dark theme
        return {
          bgPrimary: '#121212',
          bgSecondary: '#1e1e1e',
          textPrimary: '#ffffff',
          textSecondary: '#b3b3b3',
          accent: '#bb86fc',
          error: '#cf6679',
        };
    }
  };